import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { useAdminResourcePersons } from "../../../store/admin";
import { resourcePersonId } from "./resourcePersonUtils";

const EMPTY_FORM = {
  name: "",
  phone: "",
  bio: "",
};

/**
 * UPDATE — opens a modal that PATCHes this resource person
 * (PATCH /api/admin/resource-persons/:id — body: { name?, phone?, bio?, avatar? }).
 * email is read-only and must NOT be sent.
 */
const UpdateResourcePersonModal = ({ isOpen, user, onClose, onSuccess }) => {
  const {
    updateResourcePerson,
    resourcePersonMutationLoading,
  } = useAdminResourcePersons();

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [error, setError] = useState("");

  // Pre-fill the form with the selected row's current values.
  useEffect(() => {
    if (isOpen && user) {
      setFormData({
        name: user.name || "",
        phone: user.phone || "",
        bio: user.bio || "",
      });
      setError("");
    }
  }, [isOpen, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // PATCH /api/admin/resource-persons/:id
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;
    const id = resourcePersonId(user);
    if (!id) {
      setError("Unable to resolve this resource person's ID");
      return;
    }
    const result = await updateResourcePerson(id, formData);
    if (!result.success) {
      setError(result.error || "Failed to update resource person");
    } else {
      onSuccess?.();
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div
      className="modal"
      style={{
        display: "flex",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1050,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
      }}
    >
      <div
        className="modal-content"
        style={{
          backgroundColor: "#fff",
          borderRadius: "8px",
          padding: "24px",
          maxHeight: "90vh",
          overflowY: "auto",
          width: "min(90vw, 480px)",
        }}
      >
        <h5>Update Resource Person</h5>
        <form onSubmit={handleSubmit}>
          {error !== "" ? (
            <div className="formErrorDiv">
              <FontAwesomeIcon icon={faInfoCircle} />
              <p className="formError">{error}</p>
            </div>
          ) : null}
          <div className="form-group">
            <label>Full Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="form-control"
              placeholder="e.g. Chinwe Agu"
              required
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={user.email || ""}
              className="form-control"
              readOnly
              style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
            />
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="form-control"
              placeholder="e.g. +2348023456789"
            />
          </div>
          <div className="form-group">
            <label>Bio</label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              className="form-control"
              rows={3}
              placeholder="e.g. Experienced mathematics educator with 10 years of teaching experience"
            />
          </div>
          <div className="modal-buttons">
            <button type="submit" className="btn btn-theme" disabled={resourcePersonMutationLoading}>
              {resourcePersonMutationLoading ? (
                <div className="spinner-border text-light btnspinner" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                "Save Changes"
              )}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={resourcePersonMutationLoading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateResourcePersonModal;